import { getApiClient } from '@signalvote/api-client';
import { apiJson, apiVoid } from './apiFetch';
import { defineQuery, defineWrite, requireSelectedCampaign } from '../../lib/data';
import { failureFromResponse } from '@signalvote/shared';
import type {
  CampaignUserDto,
  ChangeUserRoleResponse,
  ResetVolunteerPasswordResponse,
} from '@signalvote/shared';

const STAFF_PAGE_SIZE = 500;

/**
 * The selected campaign's staff, each with their capabilities, teams and vendor. A write naming any
 * of those refreshes it.
 */
export const useVolunteers = defineQuery({
  key: () => ['volunteers', 'list'],
  reads: 'GET /api/campaigns/{id}/users',
  fetch: async (_: undefined, { campaignId }): Promise<CampaignUserDto[]> => {
    requireSelectedCampaign(campaignId);
    const data = await getApiClient().tenantCampaigns.getCampaignUsers(campaignId!, { pageSize: STAFF_PAGE_SIZE });
    return data.users;
  },
});

export const useDeleteVolunteer = defineWrite({
  send: (volunteerId: string) =>
    apiVoid(`/api/admin/volunteers/${volunteerId}`, {
      method: 'DELETE',
      error: (body, response) => failureFromResponse(response, body, body.message ? undefined : 'Failed to delete volunteer'),
    }),
});

/** Toggle a volunteer's platform admin status. */
export const useUpdateVolunteerRole = defineWrite({
  send: ({ userId, isPlatformAdmin }: { userId: string; isPlatformAdmin: boolean }) =>
    apiJson<ChangeUserRoleResponse>('/api/admin/change-user-role', {
      method: 'POST',
      json: { userId, isPlatformAdmin },
      error: (body, response) =>
        failureFromResponse(response, body, body.error ? undefined : 'Failed to update platform admin status'),
    }),
});

/** Reset a volunteer's password. No read shows it, so nothing refreshes. */
export const useResetVolunteerPassword = defineWrite({
  send: ({ volunteerId, customPassword }: { volunteerId: string; customPassword?: string | null }) =>
    apiJson<ResetVolunteerPasswordResponse>('/api/admin/reset-volunteer-password', {
      method: 'POST',
      json: { volunteerId, customPassword: customPassword || null },
      error: (body, response) => failureFromResponse(response, body, body.error ? undefined : 'Failed to reset password'),
    }),
});

export const useToggleUserStatus = defineWrite({
  send: (userId: string) =>
    apiJson<unknown>(`/api/admin/toggle-user-status/${userId}`, {
      method: 'POST',
      error: (body, response) => failureFromResponse(response, body, body.error ? undefined : 'Failed to toggle user status'),
    }),
});
